import { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { dashboardApi, savedRepliesApi } from '../../api/dashboard.api.js';
import { settingsApi } from '../../api/settings.api.js';
import { invalidateSavedReplies } from '../messages/SavedRepliesPicker.jsx';
import { EmptyState } from '../shared/EmptyState.jsx';
import { formatDateTime } from '../../utils/format.js';
import { IconClock, IconEdit, IconNote, IconTrash } from '../icons/Icons.jsx';

const ACTION_LABELS = {
  LOGIN: 'Signed in',
  CHANGED_PASSWORD: 'Changed their password',
  RESET_USER_PASSWORD: 'Reset a team member password',
  CHANGED_USER_ROLE: 'Changed a team member role',
  CREATED_CONTACT: 'Created a contact',
  DELETED_CONTACT: 'Deleted a contact',
  IMPORTED_CONTACTS: 'Imported contacts',
  BULK_SMS_SENT: 'Sent a bulk message',
  CAMPAIGN_CREATED: 'Created a campaign',
  CAMPAIGN_CANCELLED: 'Cancelled a campaign',
  CREATED_SHORT_LINK: 'Created a short link',
  DELETED_SHORT_LINK: 'Deleted a short link',
  ASSIGNED_CONVERSATION: 'Assigned a conversation',
  UPDATED_WHITE_LABEL_WORKSPACE: 'Updated workspace settings',
  UPDATED_AI_AGENT_CONFIG: 'Updated AI settings',
  AGENT_CONNECTION_CREATED: 'Connected an AI assistant',
  AGENT_CONNECTION_REVOKED: 'Disconnected an AI assistant',
  PADDLE_CHECKOUT_COMPLETED: 'Completed a payment',
  CUSTOMER_ACCEPTED_INVITE: 'Activated the workspace',
  AUTO_CONFIGURED_TWILIO_NUMBER: 'Connected a Twilio number',
  AUTO_CONFIGURED_SELECTED_IVR: 'Connected a call menu',
  ASK_AI_CREATED_SHORT_LINK: 'Created a short link with Ask AI',
  ASK_AI_IMPORTED_CONTACTS: 'Imported contacts with Ask AI',
  IMPORTED_TWILIO_KEYWORD_HISTORY: 'Imported Twilio keyword history',
};

function actionLabel(action) {
  return ACTION_LABELS[action] || action.toLowerCase().replace(/_/g, ' ').replace(/^./, (c) => c.toUpperCase());
}

export function SavedRepliesManager() {
  const [replies, setReplies] = useState(null);
  const [editing, setEditing] = useState(null); // null | 'new' | reply
  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');
  const [saving, setSaving] = useState(false);

  const load = () => savedRepliesApi.list().then(({ data }) => setReplies(data)).catch(() => setReplies([]));
  useEffect(() => { load(); }, []);

  const startEdit = (reply) => {
    setEditing(reply || 'new');
    setTitle(reply?.title || '');
    setBody(reply?.body || '');
  };

  const save = async () => {
    if (!title.trim() || !body.trim()) return toast.error('Add a title and the message text');
    setSaving(true);
    try {
      if (editing === 'new') await savedRepliesApi.create({ title, body });
      else await savedRepliesApi.update(editing.id, { title, body });
      invalidateSavedReplies();
      toast.success('Saved reply stored');
      setEditing(null);
      load();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Could not save');
    } finally {
      setSaving(false);
    }
  };

  const remove = async (reply) => {
    try {
      await savedRepliesApi.delete(reply.id);
      invalidateSavedReplies();
      setReplies((current) => current.filter((r) => r.id !== reply.id));
    } catch (err) {
      toast.error(err.response?.data?.message || 'Could not delete');
    }
  };

  return (
    <div className="space-y-4">
      <div className="card p-5">
        {editing ? (
          <div className="grid gap-3">
            <label className="grid gap-1.5 text-sm font-semibold" style={{ color: 'var(--text)' }}>
              Title
              <input className="input font-normal" value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Opening hours" maxLength={80} />
            </label>
            <label className="grid gap-1.5 text-sm font-semibold" style={{ color: 'var(--text)' }}>
              Message
              <textarea className="input font-normal" rows={4} value={body} onChange={(e) => setBody(e.target.value)} placeholder="Hi {{first_name}}, we are open Monday to Saturday, 9am to 6pm." maxLength={1600} />
            </label>
            <p className="text-xs" style={{ color: 'var(--textMuted)' }}>
              Placeholders: <code>{'{{first_name}}'}</code> <code>{'{{name}}'}</code> <code>{'{{phone}}'}</code> <code>{'{{email}}'}</code>. They are filled in when the reply is inserted.
            </p>
            <div className="flex gap-2">
              <button type="button" onClick={save} disabled={saving} className="btn-primary text-sm">{saving ? 'Saving…' : 'Save reply'}</button>
              <button type="button" onClick={() => setEditing(null)} className="btn-secondary text-sm">Cancel</button>
            </div>
          </div>
        ) : (
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
            <p className="text-sm" style={{ color: 'var(--textMuted)' }}>Your team can insert these from the message box in the inbox.</p>
            <button type="button" onClick={() => startEdit(null)} className="btn-primary text-sm">New saved reply</button>
          </div>
        )}
      </div>

      {replies === null ? (
        <div className="card h-32 animate-pulse" />
      ) : replies.length === 0 ? (
        <div className="card"><EmptyState icon={IconNote} title="No saved replies yet" text="Save answers you send often, like opening hours, prices or directions." /></div>
      ) : (
        <ul className="card divide-y" style={{ borderColor: 'var(--border)' }}>
          {replies.map((reply) => (
            <li key={reply.id} className="p-4 flex items-start gap-3" style={{ borderColor: 'var(--border)' }}>
              <div className="min-w-0 flex-1">
                <p className="text-sm font-bold" style={{ color: 'var(--text)' }}>{reply.title}</p>
                <p className="text-sm mt-0.5 whitespace-pre-wrap break-anywhere" style={{ color: 'var(--textMuted)' }}>{reply.body}</p>
              </div>
              <button type="button" onClick={() => startEdit(reply)} className="w-8 h-8 grid place-items-center rounded-lg hover:bg-[var(--neutralSoft)]" style={{ color: 'var(--textMuted)' }} aria-label={`Edit ${reply.title}`}><IconEdit size={15} /></button>
              <button type="button" onClick={() => remove(reply)} className="w-8 h-8 grid place-items-center rounded-lg hover:bg-[var(--dangerSoft)]" style={{ color: 'var(--danger)' }} aria-label={`Delete ${reply.title}`}><IconTrash size={15} /></button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

export function ActivityLogSection() {
  const [rows, setRows] = useState([]);
  const [page, setPage] = useState(1);
  const [hasNext, setHasNext] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    dashboardApi.activity(page)
      .then(({ data }) => {
        setRows((current) => (page === 1 ? data.data : [...current, ...data.data]));
        setHasNext(data.meta.hasNext);
      })
      .catch(() => toast.error('Could not load the activity log'))
      .finally(() => setLoading(false));
  }, [page]);

  if (!loading && !rows.length) {
    return <div className="card"><EmptyState icon={IconClock} title="No activity yet" text="Sign-ins, exports, campaigns and settings changes will be listed here." /></div>;
  }

  return (
    <div className="card overflow-hidden">
      <ul>
        {rows.map((row) => (
          <li key={row.id} className="px-5 py-3 flex items-start justify-between gap-4" style={{ borderBottom: '1px solid var(--border)' }}>
            <div className="min-w-0">
              <p className="text-sm" style={{ color: 'var(--text)' }}>
                <span className="font-semibold">{row.user?.name || 'System'}</span> {actionLabel(row.action).replace(/^./, (c) => c.toLowerCase())}
              </p>
              {row.user?.email && <p className="text-xs" style={{ color: 'var(--textMuted)' }}>{row.user.email}</p>}
            </div>
            <time className="text-xs flex-shrink-0" style={{ color: 'var(--textDim)' }}>{formatDateTime(row.createdAt)}</time>
          </li>
        ))}
      </ul>
      {hasNext && (
        <div className="p-4 text-center">
          <button type="button" onClick={() => setPage((p) => p + 1)} disabled={loading} className="btn-secondary text-sm">{loading ? 'Loading…' : 'Load more'}</button>
        </div>
      )}
    </div>
  );
}

export function WeeklySummaryCard() {
  const [enabled, setEnabled] = useState(null);

  useEffect(() => {
    settingsApi.getWorkspace().then(({ data }) => setEnabled(data?.weeklySummaryEnabled !== false)).catch(() => setEnabled(true));
  }, []);

  const toggle = async () => {
    const next = !enabled;
    setEnabled(next);
    try {
      await settingsApi.updateWorkspace({ weeklySummaryEnabled: next });
      toast.success(next ? 'Weekly summary turned on' : 'Weekly summary turned off');
    } catch (err) {
      setEnabled(!next);
      toast.error(err.response?.data?.message || 'Could not update');
    }
  };

  return (
    <div className="card p-5 flex items-center justify-between gap-4">
      <div>
        <p className="text-sm font-bold" style={{ color: 'var(--text)' }}>Weekly summary email</p>
        <p className="text-sm" style={{ color: 'var(--textMuted)' }}>Every Monday, workspace admins get messages sent, replies, missed calls and the biggest campaign.</p>
      </div>
      <button
        type="button"
        role="switch"
        aria-checked={Boolean(enabled)}
        disabled={enabled === null}
        onClick={toggle}
        className="relative w-11 h-6 rounded-full flex-shrink-0 transition-colors"
        style={{ background: enabled ? 'var(--accent)' : 'var(--borderStrong)' }}
      >
        <span className="absolute top-[3px] w-[18px] h-[18px] rounded-full bg-white transition-transform" style={{ left: 3, transform: enabled ? 'translateX(20px)' : 'none', boxShadow: 'var(--shadow)' }} />
        <span className="sr-only">Weekly summary email</span>
      </button>
    </div>
  );
}
